// @ts-nocheck
/**
 * Dashboard route — thin UI shell.
 *
 * All data fetching and business logic live in:
 *   src/hooks/use-dashboard.ts  (queries + derived state)
 *   src/lib/api/dashboard.ts    (supabase calls)
 *   src/lib/utils.ts            (formatSeconds, capitalize)
 *   src/components/stat-card.tsx (reusable KPI card)
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { getAppSettings } from "@/lib/api/settings";
import {
  completeOnboarding as completeOnboardingApi,
  touchLoginStreak,
  fetchProfilesWithEmail,
  fetchPopupNotifications,
  dismissNotifications,
} from "@/lib/api/users";
import { getCertificates, STUDENT_QUERY_OPTIONS, STUDENT_QUERY_KEYS } from "@/lib/api";
import { CertificateCard } from "@/components/certificates/CertificateCard";
import { AchievementsLeaderboard } from "@/components/achievements-leaderboard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { DashboardSkeleton } from "@/components/loading";
import { Badge } from "@/components/ui/badge";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/stat-card";
import { RecycleBinWidget } from "@/components/admin/recycle-bin/RecycleBinWidget";
import { UpcomingExamsWidget } from "@/components/dashboard/upcoming-exams-widget";
import { AssignmentsWidget } from "@/components/dashboard/assignments-widget";
import { useAuth } from "@/hooks/use-auth";
import { useStudentDashboard, useAdminDashboard } from "@/hooks/use-dashboard";
import { capitalize, playNotificationSound } from "@/lib/utils";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  PlayCircle,
  ShieldCheck,
  CheckCircle2,
  Clock,
  TrendingUp,
  Users,
  UserCog,
  AlertTriangle,
  BookOpen,
  ArrowRight,
  Lock,
  Sparkles,
  Phone,
  User,
  Info,
  Megaphone,
  Award,
  Trophy,
  GraduationCap,
  X,
} from "lucide-react";

import { AdminDashboardView } from "@/components/admin/dashboard/AdminDashboardView";

export const Route = createFileRoute("/_authenticated/home")({
  component: DashboardPage,
});

function DashboardPage() {
  const { hasPermission, user, isTeamLeader } = useAuth();
  const navigate = useNavigate();

  const isContentCreatorLanding =
    hasPermission("manage:courses") &&
    !hasPermission("view:roster") &&
    !hasPermission("manage:users");

  const isTeamLeaderLanding = isTeamLeader && !hasPermission("manage:users");

  useEffect(() => {
    if (isContentCreatorLanding) {
      navigate({ to: "/admin/courses" });
    }
  }, [isContentCreatorLanding, navigate]);

  useEffect(() => {
    if (isTeamLeaderLanding) {
      navigate({ to: "/team-leader/dashboard" });
    }
  }, [isTeamLeaderLanding, navigate]);

  if (isContentCreatorLanding || isTeamLeaderLanding) return null;

  return hasPermission("view:analytics") || hasPermission("manage:users") ? (
    <AdminDashboardView />
  ) : (
    <StudentDashboard userId={user?.id ?? ""} />
  );
}

/** Render admin-authored welcome text: `**bold**` segments become <strong>, everything else stays plain. */
function renderMarkdownBold(text: string | undefined): ReactNode {
  if (!text) return null;
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

/* ─────────────────────────── STUDENT VIEW ─────────────────────────── */

function StudentDashboard({ userId }: { userId: string }) {
  const { vm, isLoading } = useStudentDashboard(userId);
  const qc = useQueryClient();

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [dismissingIds, setDismissingIds] = useState<string[]>([]);

  // Admin-configurable welcome message (with per-project overrides)
  const studentProject = (vm?.profile as any)?.project_assigned ?? null;
  const { data: welcomeMessage } = useQuery({
    queryKey: ["app_settings", "welcome_message", studentProject],
    queryFn: async () => {
      const data = await getAppSettings("welcome_message");
      const v: any = data ?? {};
      const byProject = (v.byProject ?? {}) as Record<string, any>;
      const override = studentProject ? byProject[studentProject] : null;
      const pick = (k: string) => override?.[k] ?? v[k];
      return {
        title: pick("title") || "Welcome!",
        body1:
          pick("body1") ||
          "Welcome to your **Training & Communication Hub**! We're thrilled to have you join our cohort.",
        body2:
          pick("body2") ||
          "This dashboard is your home base for training courses, tracking achievements, certificates, and communicating with your team.",
        note:
          pick("note") || "Please take a moment to confirm your profile information to proceed.",
      };
    },
  });

  // Undismissed new-message/new-announcement notifications, server-persisted (Notification.
  // IsDismissed/DismissedAt) — this is the single source of truth for "has the student already
  // seen and closed this popup," so it survives refreshes, re-logins, and other devices. Nothing
  // here is derived from localStorage.
  // Its OWN key — sharing the bell's key made this popup render the bell's unfiltered list
  // (all types, including already-dismissed rows), so it could never stop reappearing.
  const popupQueryKey = STUDENT_QUERY_KEYS.popupNotifications(userId);
  const { data: popupNotifications = [] } = useQuery({
    queryKey: popupQueryKey,
    queryFn: fetchPopupNotifications,
    enabled: !!userId,
    ...STUDENT_QUERY_OPTIONS,
  });

  const hasUnread = popupNotifications.length > 0;
  const showUnreadPopup = hasUnread && dismissingIds.length === 0;

  // Play the sound once per newly-arrived batch, not on every re-render while the popup is open.
  const soundPlayedRef = useRef(false);
  useEffect(() => {
    if (hasUnread && !soundPlayedRef.current) {
      soundPlayedRef.current = true;
      playNotificationSound();
    } else if (!hasUnread) {
      soundPlayedRef.current = false;
    }
  }, [hasUnread]);

  const dismissMut = useMutation({
    mutationFn: async (ids: string[]) => {
      setDismissingIds(ids);
      await dismissNotifications(ids);
    },
    onSuccess: (_data, ids) => {
      // Update immediately so the dialog closes without waiting on a refetch (no flicker), then
      // reconcile with the server in the background.
      qc.setQueryData(popupQueryKey, (old: any[] = []) => old.filter((n) => !ids.includes(n.id)));
      qc.invalidateQueries({ queryKey: popupQueryKey });
      // Dismissing also sets IsRead server-side, so the bell's unread badge is now stale.
      // Invalidated explicitly: the bell's key is a PREFIX of the popup's, and invalidating the
      // longer key does not match the shorter one.
      qc.invalidateQueries({ queryKey: STUDENT_QUERY_KEYS.notifications(userId) });
      setDismissingIds([]);
    },
    onError: () => {
      setDismissingIds([]);
      toast.error("Failed to update message status — it may reappear on refresh.");
    },
  });

  const dismissPopup = () => {
    if (dismissMut.isPending || popupNotifications.length === 0) return;
    dismissMut.mutate(popupNotifications.map((n: any) => n.id));
  };

  const profile = vm?.profile;

  // Persistent welcome banner for returning students (onboarding dialog above only ever shows to
  // brand-new users). Dismissal is remembered per-message so editing the admin message re-surfaces it.
  const welcomeSignature = welcomeMessage
    ? [welcomeMessage.title, welcomeMessage.body1, welcomeMessage.body2, welcomeMessage.note].join(
        "|",
      )
    : null;
  const [dismissedWelcomeSignature, setDismissedWelcomeSignature] = useState<string | null>(null);
  useEffect(() => {
    if (!userId) return;
    setDismissedWelcomeSignature(localStorage.getItem(`welcome_message_dismissed_${userId}`));
  }, [userId]);
  const showWelcomeBanner =
    !!welcomeSignature && welcomeSignature !== dismissedWelcomeSignature && !showOnboarding;
  function dismissWelcomeBanner() {
    if (welcomeSignature) {
      localStorage.setItem(`welcome_message_dismissed_${userId}`, welcomeSignature);
      setDismissedWelcomeSignature(welcomeSignature);
    }
  }

  // Initialize onboarding fields
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || "");
      setPhone(profile.phone || "");
      if (profile.onboarding_completed === false) {
        setShowOnboarding(true);
      }
    }
  }, [profile]);

  // Update streak logic — the server computes the actual streak value (UTC-day diff) and is
  // idempotent per day, so this just needs to fire once per dashboard load.
  useEffect(() => {
    if (!profile || !userId) return;
    touchLoginStreak().then((ok) => {
      if (ok) qc.invalidateQueries({ queryKey: ["student-dashboard", userId] });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const completeOnboarding = useMutation({
    mutationFn: async () => {
      const ok = await completeOnboardingApi(fullName, phone);
      if (!ok) throw new Error("Update profile failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["student-dashboard", userId] });
      toast.success("Welcome aboard, " + fullName.split(" ")[0] + "!");
      setShowOnboarding(false);
    },
    onError: (err: any) => {
      toast.error("Error completing onboarding: " + err.message);
    },
  });

  if (isLoading || !vm) return <DashboardSkeleton />;

  const {
    firstName,
    completedModules,
    totalModules,
    overallProgress,
    timeInvested,
    nextModule,
    allComplete,
    examPassed,
    examTaken,
    modules,
    progress,
    examSession,
    upcomingExams,
  } = vm;

  function getStatus(moduleId: string) {
    return progress.find((p) => p.module_id === moduleId)?.status ?? "not_started";
  }

  // Human-facing module numbers come from POSITION in the ordered list, never from the raw
  // sequence_order (the backend's OrderIndex): that is 0-based, so the first module rendered
  // as "0.", and it keeps gaps after a module is deleted (0, 1, 3). The server already sorts
  // modules by OrderIndex, so position is the correct 1-based number — matching how the admin
  // editor and the training path number their steps.
  const nextModuleNumber = nextModule ? modules.findIndex((m) => m.id === nextModule.id) + 1 : 0;

  return (
    <div className="space-y-6">
      {/* Welcome Banner using Brand Palette (Navy to Gold Accent) */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary via-primary/95 to-accent/90 text-white rounded-3xl p-6 md:p-8 shadow-lg animate-in fade-in slide-in-from-top-4 duration-500">
        {/* Decorative background shape */}
        <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 opacity-10 pointer-events-none">
          <GraduationCap
            className="w-64 h-64 text-white animate-pulse"
            style={{ animationDuration: "8s" }}
          />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-accent animate-pulse shrink-0" />
              <h1 className="text-3xl font-extrabold tracking-tight">Welcome back, {firstName}</h1>
            </div>
            <p className="mt-2 text-primary-foreground/90 text-sm md:text-base max-w-xl leading-relaxed">
              Track your modules progress, take exams, and view your earned certifications.
            </p>
          </div>
        </div>
      </div>

      {showWelcomeBanner && (
        <Card className="relative border-primary/20 bg-primary/5 animate-in fade-in slide-in-from-top-2 duration-500">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 h-7 w-7 text-muted-foreground"
            onClick={dismissWelcomeBanner}
            aria-label="Dismiss welcome message"
          >
            <X className="h-4 w-4" />
          </Button>
          <CardContent className="pt-6 space-y-2 pr-8">
            <div className="flex items-center gap-2 font-bold text-primary">
              <Sparkles className="h-4 w-4 text-accent shrink-0" />
              {welcomeMessage?.title}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
              {renderMarkdownBold(welcomeMessage?.body1)}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
              {renderMarkdownBold(welcomeMessage?.body2)}
            </p>
            {welcomeMessage?.note && (
              <div className="rounded-lg bg-primary/10 p-3 flex items-start gap-2 border text-xs">
                <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span className="text-muted-foreground whitespace-pre-line">
                  {renderMarkdownBold(welcomeMessage.note)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {examPassed && (
        <div className="rounded-2xl border border-success/20 bg-success-muted px-4 py-3.5 shadow-sm animate-in zoom-in-95 duration-300 flex items-center gap-3">
          <div className="p-1.5 bg-success/15 rounded-lg text-success shrink-0 animate-pulse">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div>
            <p className="font-bold text-sm text-success">You are fully certified!</p>
            <p className="text-xs text-success/80 mt-0.5 font-medium">
              Score: {examSession!.score}% · Completed{" "}
              {new Date(examSession!.completed_at!).toLocaleDateString()}
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <StatCard
          icon={<PlayCircle className="h-5 w-5" />}
          label="Modules Completed"
          value={`${completedModules} / ${totalModules}`}
          sub={`${overallProgress}% complete`}
          color="primary"
        />
        <StatCard
          icon={<ShieldCheck className="h-5 w-5" />}
          label="Exam Status"
          value={examPassed ? "Passed" : examTaken ? "Not passed yet" : "Not taken"}
          sub={
            examPassed
              ? `Score: ${examSession!.score}%`
              : allComplete
                ? "Ready to take"
                : "Complete training first"
          }
          color={examPassed ? "green" : "amber"}
        />
        <StatCard
          icon={<Clock className="h-5 w-5" />}
          label="Time Invested"
          value={timeInvested}
          sub="Total watch time"
          color="secondary"
        />
        <StatCard
          icon={<BookOpen className="h-5 w-5" />}
          label="Training Status"
          value={profile?.training_status ?? "Not set"}
          sub={`Account: ${capitalize(profile?.status ?? "pending")}`}
          color={
            profile?.training_status === "Passed Training"
              ? "green"
              : profile?.training_status === "In Training"
                ? "primary"
                : profile?.status === "approved"
                  ? "green"
                  : "amber"
          }
        />
      </div>

      <UpcomingExamsWidget exams={upcomingExams ?? []} />

      <AssignmentsWidget />

      <div className="rounded-2xl border bg-card p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-bold text-lg text-foreground flex items-center gap-2">
            <PlayCircle className="h-5 w-5 text-primary" />
            Training Progress
          </h2>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="gap-1 text-primary hover:bg-primary/5 rounded-lg text-xs font-semibold"
          >
            <Link to="/training">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>

        <div className="mb-4 flex items-center gap-3">
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${overallProgress}%`, background: "var(--gradient-brand)" }}
            />
          </div>
          <span className="text-sm font-bold text-muted-foreground">{overallProgress}%</span>
        </div>

        <div className="space-y-1">
          {modules.slice(0, 5).map((mod, index) => {
            const status = getStatus(mod.id);
            return (
              <div
                key={mod.id}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-muted/40 transition-all duration-200 group/item cursor-pointer animate-in fade-in"
                style={{ animationDelay: `${index * 30}ms`, animationFillMode: "both" }}
              >
                {status === "completed" ? (
                  <div className="p-1 bg-green-500/10 rounded-lg text-green-500 shrink-0">
                    <CheckCircle2 className="h-4 w-4" />
                  </div>
                ) : status === "in_progress" ? (
                  <div className="p-1 bg-primary/10 rounded-lg text-primary shrink-0 animate-pulse">
                    <PlayCircle className="h-4 w-4" />
                  </div>
                ) : (
                  <div className="p-1 bg-muted rounded-lg text-muted-foreground/40 shrink-0">
                    <Lock className="h-4 w-4" />
                  </div>
                )}
                <span
                  className={`flex-1 text-sm font-medium transition-colors group-hover/item:text-foreground ${
                    status === "not_started" ? "text-muted-foreground" : "text-foreground"
                  }`}
                >
                  {index + 1}. {mod.title}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {!examPassed && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary to-primary/95 text-white p-6 shadow-md border border-primary/10 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="absolute right-0 bottom-0 translate-y-6 translate-x-6 opacity-5 pointer-events-none">
            <Trophy className="w-48 h-48 text-white" />
          </div>

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-[10px] text-accent font-bold uppercase tracking-wider">
                Next Milestone
              </p>
              {!allComplete && nextModule ? (
                <>
                  <h4 className="font-extrabold text-lg mt-1">
                    Continue Module {nextModuleNumber}
                  </h4>
                  <p className="mt-1 text-sm text-primary-foreground/85 font-medium">
                    {nextModule.title}
                  </p>
                </>
              ) : allComplete ? (
                <>
                  <h4 className="font-extrabold text-lg mt-1">Ready for Safe Exam</h4>
                  <p className="mt-1 text-sm text-primary-foreground/85 font-medium">
                    All training modules completed successfully.
                  </p>
                </>
              ) : null}
            </div>

            {!allComplete && nextModule ? (
              <Button
                asChild
                size="sm"
                className="bg-white hover:bg-white/90 text-primary font-bold rounded-xl shrink-0 group/btn transition-all duration-300"
              >
                <Link to="/training" className="inline-flex items-center gap-1.5">
                  Continue Training
                  <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
                </Link>
              </Button>
            ) : allComplete ? (
              <Button
                asChild
                size="sm"
                className="bg-accent hover:bg-accent/90 text-primary font-black rounded-xl shrink-0 group/btn transition-all duration-300"
              >
                <Link to="/exams" className="inline-flex items-center gap-1.5">
                  Take Safe Exam
                  <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" />
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
      )}

      <RecentCertificatesWidget userId={userId} />

      {/* No batch picker: the server pins a student to their own batch, and the card names it. */}
      <AchievementsLeaderboard
        currentUserId={userId}
        top={10}
        // True whether or not the student is in a batch: when they are, the card's badge names it;
        // when they are not, the server falls back to the whole-platform board.
        description="Where you stand among your fellow students."
      />

      <Dialog open={showOnboarding} onOpenChange={setShowOnboarding}>
        <DialogContent className="max-w-md p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              <Sparkles className="h-5 w-5 text-accent animate-pulse" />
              {onboardingStep === 1 && (welcomeMessage?.title ?? "Welcome!")}
              {onboardingStep === 2 && "Configure Profile"}
              {onboardingStep === 3 && "Getting Certified"}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {onboardingStep === 1 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {renderMarkdownBold(welcomeMessage?.body1)}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                  {renderMarkdownBold(welcomeMessage?.body2)}
                </p>
                <div className="rounded-lg bg-primary/5 p-3 flex items-start gap-2 border text-xs">
                  <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-muted-foreground whitespace-pre-line">
                    {renderMarkdownBold(welcomeMessage?.note)}
                  </span>
                </div>
              </div>
            )}

            {onboardingStep === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ob-name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="ob-name"
                      className="pl-9"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Jane Doe"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ob-phone">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="ob-phone"
                      className="pl-9"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="e.g. +20 123 456 789"
                    />
                  </div>
                </div>
              </div>
            )}

            {onboardingStep === 3 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  To complete certification and start training, follow this roadmap:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2.5 text-xs text-muted-foreground">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">
                      1
                    </span>
                    <span>
                      <strong>Complete Course:</strong> Complete all video and document modules.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5 text-xs text-muted-foreground">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">
                      2
                    </span>
                    <span>
                      <strong>Pass Exam:</strong> Take the Safe Exam with biometric selfie check.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5 text-xs text-muted-foreground">
                    <span className="h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">
                      3
                    </span>
                    <span>
                      <strong>Graduate:</strong> Admins approve your profile, and you receive your
                      certificate!
                    </span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div className="flex justify-between mt-6">
            <Button
              variant="ghost"
              size="sm"
              disabled={onboardingStep === 1}
              onClick={() => setOnboardingStep((s) => s - 1)}
            >
              Back
            </Button>
            {onboardingStep < 3 ? (
              <Button
                size="sm"
                disabled={onboardingStep === 2 && !fullName.trim()}
                onClick={() => setOnboardingStep((s) => s + 1)}
              >
                Next
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={completeOnboarding.isPending}
                onClick={() => completeOnboarding.mutate()}
              >
                {completeOnboarding.isPending ? "Starting..." : "Let's Begin!"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showUnreadPopup}
        onOpenChange={(open) => {
          // Closing via X/outside-click/Escape must dismiss exactly like the button — otherwise
          // it would reopen on the next refresh even though the student already saw it.
          if (!open) dismissPopup();
        }}
      >
        <DialogContent className="max-w-md p-6 bg-card border rounded-xl shadow-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-bold">
              <Megaphone className="h-5 w-5 text-amber-500 animate-bounce" />
              New Announcements!
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              You have new unread messages/announcements:
            </p>

            <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
              {popupNotifications.map((n: any) => (
                <div
                  key={n.id}
                  className="rounded-lg border bg-muted/20 p-3 space-y-1.5 text-xs text-foreground"
                >
                  <div className="flex items-center justify-between font-semibold text-primary">
                    <span>{n.title}</span>
                    <span className="text-[10px] text-muted-foreground font-normal">
                      {new Date(n.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {n.body && (
                    <p className="whitespace-pre-wrap leading-relaxed text-muted-foreground italic">
                      "{n.body}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={dismissMut.isPending}
              onClick={dismissPopup}
            >
              {dismissMut.isPending ? "Saving…" : "Dismiss"}
            </Button>
            <Button asChild size="sm" disabled={dismissMut.isPending}>
              <Link to="/messages" onClick={dismissPopup}>
                Go to Messages
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecentCertificatesWidget({ userId }: { userId: string }) {
  const { data: certificates = [], isLoading } = useQuery({
    queryKey: STUDENT_QUERY_KEYS.userCertificates(userId),
    queryFn: () => getCertificates(userId),
    enabled: !!userId,
    ...STUDENT_QUERY_OPTIONS,
  });

  const recentCerts = useMemo(() => {
    return [...certificates]
      .filter((c) => c.status === "issued")
      .sort(
        (a, b) =>
          new Date(b.issued_at || b.created_at).getTime() -
          new Date(a.issued_at || a.created_at).getTime(),
      )
      .slice(0, 3);
  }, [certificates]);

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card p-5 shadow-[var(--shadow-card)] space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Recent Certificates</h2>
          <Skeleton className="h-8 w-20" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-56 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (recentCerts.length === 0) return null;

  return (
    <div className="rounded-xl border bg-card p-5 shadow-[var(--shadow-card)] space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-foreground flex items-center gap-2">
          <Award className="h-5 w-5 text-primary" />
          Recent Certificates
        </h2>
        <Button asChild variant="ghost" size="sm" className="gap-1 text-primary">
          <Link to="/certificates">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {recentCerts.map((cert) => (
          <CertificateCard key={cert.id} certificate={cert} />
        ))}
      </div>
    </div>
  );
}
